import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const AboutContainer = styled.section`
  padding: 6rem 0;
  position: relative;
  background: ${({ theme }) => theme.colors.background.primary};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 3rem 0;
  }
`;

const AboutContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 1.5rem;
    padding: 0 1rem;
  }
`;

const AboutText = styled.div`
  z-index: 2;
`;

const AboutTitle = styled(motion.h2)`
  font-size: 3rem;
  margin-bottom: 2rem;
  background: ${({ theme }) => theme.gradients.primary};
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 800;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
  }
`;

const AboutDescription = styled(motion.p)`
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 2rem;
  font-size: 1.1rem;
  line-height: 1.7;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1rem;
    line-height: 1.6;
    margin-bottom: 1.5rem;
    padding: 0 0.5rem;
  }
`;

const FeaturesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 1rem;
  }
`;

const FeatureItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 10px;
  border-left: 4px solid ${({ theme }) => theme.colors.primary.yellow};
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 229, 0, 0.1);
    transform: translateX(10px);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 0.75rem;
    gap: 0.75rem;
    margin: 0 0.5rem;
    
    &:hover {
      transform: none;
    }
  }
`;

const FeatureIcon = styled.div`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.primary.yellow};
  min-width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.3rem;
    min-width: 35px;
  }
`;

const FeatureContent = styled.div`
  h3 {
    color: ${({ theme }) => theme.colors.primary.yellow};
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
    font-weight: 600;
  }
  
  p {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: 0.95rem;
    line-height: 1.5;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    h3 {
      font-size: 1rem;
      margin-bottom: 0.4rem;
    }
    
    p {
      font-size: 0.9rem;
      line-height: 1.4;
    }
  }
`;

const AboutIllustration = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 500px;
  position: relative;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    height: 350px;
  }
`;

const NetworkHub = styled(motion.div)`
  position: relative;
  width: 400px;
  height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 300px;
    height: 300px;
  }
`;

const CentralNode = styled(motion.div)`
  position: absolute;
  width: 100px;
  height: 100px;
  background: ${({ theme }) => theme.gradients.cyber};
  border-radius: 50%;
  border: 3px solid ${({ theme }) => theme.colors.primary.yellow};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  box-shadow: 0 0 40px rgba(255, 229, 0, 0.6);
  z-index: 10;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 80px;
    height: 80px;
    font-size: 1.7rem;
    border-width: 2px;
    box-shadow: 0 0 30px rgba(255, 229, 0, 0.5);
  }
`;

const OrbitRing = styled(motion.div)`
  position: absolute;
  border: 2px solid ${({ color, theme }) => color || theme.colors.primary.blue};
  border-radius: 50%;
  opacity: 0.4;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    border-width: 1px;
    opacity: 0.3;
  }
`;

const SatelliteNode = styled(motion.div)`
  position: absolute;
  width: 60px;
  height: 60px;
  background: ${({ theme }) => theme.gradients.card};
  border: 2px solid ${({ theme }) => theme.colors.primary.blue};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 45px;
    height: 45px;
    font-size: 1.2rem;
    border-width: 1px;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
  }
`;

const ConnectionLine = styled(motion.div)`
  position: absolute;
  height: 2px;
  background: linear-gradient(90deg, transparent, ${({ theme }) => theme.colors.primary.cyan}, transparent);
  border-radius: 1px;
  opacity: 0.6;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    height: 1px;
    opacity: 0.5;
  }
`;

const DataPulse = styled(motion.div)`
  position: absolute;
  width: 8px;
  height: 8px;
  background: ${({ theme }) => theme.colors.primary.yellow};
  border-radius: 50%;
  box-shadow: 0 0 15px ${({ theme }) => theme.colors.primary.yellow};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 6px;
    height: 6px;
    box-shadow: 0 0 10px ${({ theme }) => theme.colors.primary.yellow};
  }
`;

// Team Section Styles
const TeamSection = styled.section`
  padding: 4rem 0;
  background: ${({ theme }) => theme.colors.surface};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 2rem 0;
  }
`;

const TeamHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-bottom: 2rem;
    padding: 0 1rem;
  }
`;

const TeamTitle = styled(motion.h2)`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  background: ${({ theme }) => theme.gradients.primary};
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 800;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 2rem;
    margin-bottom: 0.75rem;
  }
`;

const TeamSubtitle = styled(motion.p)`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 1.1rem;
  max-width: 600px;
  margin: 0 auto;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1rem;
    padding: 0 1rem;
  }
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    padding: 0 1rem;
  }
`;

const TeamCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border};
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  
  &:hover {
    transform: translateY(-8px);
    border-color: ${({ theme }) => theme.colors.primary.yellow};
    box-shadow: 0 12px 30px rgba(255, 229, 0, 0.2);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    border-radius: 15px;
    
    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 20px rgba(255, 229, 0, 0.15);
    }
  }
`;

const TeamImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 300px;
  overflow: hidden;
  background: ${({ theme }) => theme.gradients.cyber};
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%);
    animation: imageShine 3s ease-in-out infinite;
  }
  
  @keyframes imageShine {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    height: 240px;
  }
`;

const TeamImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
  
  ${TeamCard}:hover & {
    transform: scale(1.05);
  }
`;

const TeamImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
  color: ${({ theme }) => theme.colors.primary.yellow};
  background: ${({ theme }) => theme.gradients.cyber};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 3rem;
  }
`;

const TeamInfo = styled.div`
  padding: 1.5rem;
  text-align: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 1.25rem;
  }
`;

const TeamName = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 0.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.2rem;
    margin-bottom: 0.4rem;
  }
`;

const TeamRole = styled.p`
  color: ${({ theme }) => theme.colors.primary.yellow};
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.95rem;
    margin-bottom: 0.5rem;
  }
`;

const TeamBio = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.9rem;
  line-height: 1.5;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.85rem;
    line-height: 1.4;
  }
`;

const About = () => {
  const features = [
    {
      icon: '🤝',
      title: 'Join Community',
      description: 'Connect with startups, security, and IT professionals from around the world.'
    },
    {
      icon: '🧠',
      title: 'New Knowledge',
      description: 'Learn the latest cybersecurity techniques from industry experts and speakers.'
    },
    {
      icon: '💡',
      title: 'Idea Sharing',
      description: 'Share cybersecurity and IT ideas with every participant in the conference.'
    },
    {
      icon: '🎯',
      title: 'Hacking Village',
      description: 'Participate in interactive workshops and practical cybersecurity challenges.'
    }
  ];

  const teamMembers = [
    {
      name: 'Saad Nasir',
      role: 'Founder & Organizer',
      image: 'Saad Nasir.jpeg',
      icon: '👨‍💼',
      bio: 'Passionate cybersecurity advocate driving community engagement and innovation.'
    },
    {
      name: 'Gabrielle Trujillo',
      role: 'Co-Founder',
      image: 'Gabrielle Trujillo.jpeg',
      icon: '👩‍💼',
      bio: 'Experienced leader in cybersecurity education and community building.'
    },
    {
      name: 'Kristen Sanders',
      role: 'Organizer',
      image: 'Kristen Sanders.jpeg',
      icon: '👩‍🔧',
      bio: 'Organizer and speaker championing cyber resilience.'
    },
    {
      name: 'Ashley Sequeira',
      role: 'Chief Security Officer',
      image: 'Ashley Sequeira.jpeg',
      icon: '🛡️',
      bio: 'Strategic security leader with extensive industry experience.'
    },
    {
      name: 'Chris Perkins',
      role: 'Organizer & Communication Director',
      image: 'Chris Perkins.jpeg',
      icon: '📢',
      bio: 'Communication expert bridging technical and business communities.'
    }
  ];

  const satellites = [
    { id: 1, icon: '🔐', angle: 0, radius: 140 },
    { id: 2, icon: '🛡️', angle: 72, radius: 140 },
    { id: 3, icon: '⚡', angle: 144, radius: 140 },
    { id: 4, icon: '🔍', angle: 216, radius: 140 },
    { id: 5, icon: '🌐', angle: 288, radius: 140 },
  ];

  const orbitRings = [
    { radius: 120, color: '#FFE500', duration: 20 },
    { radius: 160, color: '#0080D6', duration: 25 },
    { radius: 200, color: '#9B59B6', duration: 30 },
  ];

  // Helper function to get image path
  const getImagePath = (imageFile) => {
    return `/about/${imageFile}`;
  };

  // Adjust satellite radius for mobile
  const adjustedSatellites = satellites.map(sat => ({
    ...sat,
    radius: typeof window !== 'undefined' && window.innerWidth <= 768 ? sat.radius * 0.75 : sat.radius
  }));

  const adjustedOrbitRings = orbitRings.map(ring => ({
    ...ring,
    radius: typeof window !== 'undefined' && window.innerWidth <= 768 ? ring.radius * 0.75 : ring.radius
  }));

  return (
    <>
      <AboutContainer id="about">
        <div className="container">
          <AboutContent>
            <AboutText>
              <AboutTitle
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                About Security BSides Albuquerque 2025
              </AboutTitle>
              
              <AboutDescription
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
              >
                We're thrilled to host Security BSides Albuquerque 2025, a community-driven 
                event where cybersecurity enthusiasts and experts come together to share 
                knowledge, exchange ideas, and strengthen our collective security awareness 
                in a collaborative and inclusive environment.
              </AboutDescription>
              
              <FeaturesList>
                {features.map((feature, index) => (
                  <FeatureItem
                    key={feature.title}
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <FeatureIcon>{feature.icon}</FeatureIcon>
                    <FeatureContent>
                      <h3>{feature.title}</h3>
                      <p>{feature.description}</p>
                    </FeatureContent>
                  </FeatureItem>
                ))}
              </FeaturesList>
            </AboutText>
            
            <AboutIllustration>
              <NetworkHub>
                {/* Orbit Rings */}
                {adjustedOrbitRings.map((ring, index) => (
                  <OrbitRing
                    key={index}
                    style={{
                      width: `${ring.radius * 2}px`,
                      height: `${ring.radius * 2}px`,
                    }}
                    color={ring.color}
                    duration={ring.duration}
                    direction={index % 2 === 0 ? 'normal' : 'reverse'}
                    initial={{ opacity: 0, scale: 0.5 }}
                    whileInView={{ opacity: 0.4, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5 + index * 0.2, duration: 0.8 }}
                  />
                ))}
                
                {/* Central Node */}
                <CentralNode
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 1, delay: 0.8 }}
                  viewport={{ once: true }}
                  style={{
                    animation: 'smoothFloat 4s ease-in-out infinite'
                  }}
                >
                  🛡️
                </CentralNode>
                
                {/* Satellite Nodes */}
                {adjustedSatellites.map((satellite, index) => {
                  const x = Math.cos((satellite.angle * Math.PI) / 180) * satellite.radius;
                  const y = Math.sin((satellite.angle * Math.PI) / 180) * satellite.radius;
                  
                  return (
                    <SatelliteNode
                      key={satellite.id}
                      style={{
                        left: `calc(50% + ${x}px)`,
                        top: `calc(50% + ${y}px)`,
                        transform: 'translate(-50%, -50%)',
                      }}
                      delay={index * 0.5}
                      initial={{ opacity: 0, scale: 0 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.6, delay: 1.2 + index * 0.1 }}
                      viewport={{ once: true }}
                    >
                      {satellite.icon}
                    </SatelliteNode>
                  );
                })}
                
                {/* Connection Lines */}
                {adjustedSatellites.map((satellite, index) => {
                  const x = Math.cos((satellite.angle * Math.PI) / 180) * (satellite.radius / 2);
                  const y = Math.sin((satellite.angle * Math.PI) / 180) * (satellite.radius / 2);
                  const length = satellite.radius / 2;
                  
                  return (
                    <ConnectionLine
                      key={`line-${satellite.id}`}
                      style={{
                        width: `${length}px`,
                        left: `calc(50% + ${x}px)`,
                        top: `calc(50% + ${y}px)`,
                        transform: `translate(-50%, -50%) rotate(${satellite.angle}deg)`,
                        transformOrigin: 'left center',
                      }}
                      initial={{ scaleX: 0, opacity: 0 }}
                      whileInView={{ scaleX: 1, opacity: 0.6 }}
                      transition={{ duration: 1, delay: 1.8 + index * 0.1 }}
                      viewport={{ once: true }}
                    />
                  );
                })}
                
                {/* Data Pulses */}
                {adjustedSatellites.map((satellite, index) => {
                  const startX = Math.cos((satellite.angle * Math.PI) / 180) * satellite.radius;
                  const startY = Math.sin((satellite.angle * Math.PI) / 180) * satellite.radius;
                  
                  return (
                    <DataPulse
                      key={`pulse-${satellite.id}`}
                      style={{
                        left: `calc(50% + ${startX}px)`,
                        top: `calc(50% + ${startY}px)`,
                        transform: 'translate(-50%, -50%)',
                      }}
                      animate={{
                        x: [-startX, -startX * 0.1],
                        y: [-startY, -startY * 0.1],
                        opacity: [1, 0],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        delay: 3 + index * 0.4,
                        ease: "easeOut"
                      }}
                    />
                  );
                })}
                
                {/* Background Grid */}
                <motion.div
                  style={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    backgroundImage: `
                      linear-gradient(rgba(155, 89, 182, 0.1) 1px, transparent 1px),
                      linear-gradient(90deg, rgba(155, 89, 182, 0.1) 1px, transparent 1px)
                    `,
                    backgroundSize: typeof window !== 'undefined' && window.innerWidth <= 768 ? '20px 20px' : '30px 30px',
                    opacity: 0.3,
                    borderRadius: '20px',
                  }}
                  animate={{
                    backgroundPosition: typeof window !== 'undefined' && window.innerWidth <= 768 ? ['0px 0px', '20px 20px'] : ['0px 0px', '30px 30px'],
                  }}
                  transition={{
                    duration: 20,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                />
              </NetworkHub>
            </AboutIllustration>
          </AboutContent>
        </div>
      </AboutContainer>

      {/* Team Section */}
      <TeamSection>
        <div className="container">
          <TeamHeader>
            <TeamTitle
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              Meet Our Team
            </TeamTitle>
            <TeamSubtitle
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              The passionate individuals behind Security BSides Albuquerque 2025
            </TeamSubtitle>
          </TeamHeader>

          <TeamGrid>
            {teamMembers.map((member, index) => (
              <TeamCard
                key={member.name}
                initial={{ opacity: 0, y: 30, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02 }}
              >
                <TeamImageContainer>
                  <TeamImage
                    src={getImagePath(member.image)}
                    alt={member.name}
                    onError={(e) => {
                      // Hide the image and show placeholder on error
                      e.target.style.display = 'none';
                      const placeholder = e.target.parentNode.querySelector('.placeholder');
                      if (placeholder) {
                        placeholder.style.display = 'flex';
                      }
                    }}
                  />
                  <TeamImagePlaceholder className="placeholder" style={{ display: 'none' }}>
                    {member.icon}
                  </TeamImagePlaceholder>
                </TeamImageContainer>
                <TeamInfo>
                  <TeamName>{member.name}</TeamName>
                  <TeamRole>{member.role}</TeamRole>
                  <TeamBio>{member.bio}</TeamBio>
                </TeamInfo>
              </TeamCard>
            ))}
          </TeamGrid>
        </div>
      </TeamSection>
    </>
  );
};

export default About;